import { FC, useEffect } from 'react';
import { localStorage } from 'dibs-browser-storage';
import { FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import { STORAGE, VARS } from '../../constants/checkoutConstants';
import LocalSpinner from '../../shared/components/LocalSpinner';

import { CheckoutProcessingPage_item$key } from './__generated__/CheckoutProcessingPage_item.graphql';

import styles from './styles/CheckoutProcessingPage.scss';

type Props = {
    item: CheckoutProcessingPage_item$key;
};

const fragment = graphql`
    fragment CheckoutProcessingPage_item on Item {
        isMultiSku
        title
        mediumPhoto: firstPhotoWebPath(size: medium)
    }
`;

function getAppAdViews(): number {
    const value = localStorage.getItem(STORAGE.APP_AD_VIEW) as string;
    return value ? parseInt(value) : 0;
}

const CheckoutProcessingPage: FC<Props> = ({ item: itemRef }) => {
    const item = useFragment(fragment, itemRef);
    const isMultiSku = item?.isMultiSku;
    const shouldRenderAppAd = getAppAdViews() <= VARS.MAX_APP_AD_VIEWS;

    useEffect(() => {
        if (shouldRenderAppAd) {
            localStorage.setItem(STORAGE.APP_AD_VIEW, getAppAdViews() + 1);
        }
    }, [shouldRenderAppAd]);

    const renderDefault = (
        <div className={styles.itemContainer}>
            <hr className={styles.imageBar} />
            <img alt={item.title || ''} src={item.mediumPhoto || ''} className={styles.itemImage} />
        </div>
    );

    const renderAppAd = (
        <div className={styles.phoneContainer}>
            <img
                alt="iPhone"
                className={styles.phoneImage}
                src="https://a.1stdibscdn.com/dist/adhoc/checkout/iphone-home.png"
            />
            <div className={styles.phoneMessages}>
                <p className={styles.textMillerRegular}>
                    <FormattedMessage
                        id="checkout.CheckoutProcessingAdPage.line1"
                        defaultMessage="Browsing made more"
                    />
                </p>
                <p className={styles.textMillerItalic}>
                    <FormattedMessage
                        id="checkout.CheckoutProcessingAdPage.line2"
                        defaultMessage="beautiful"
                    />
                </p>
                <p className={styles.textMillerRegular}>
                    <FormattedMessage
                        id="checkout.CheckoutProcessingAdPage.line3"
                        defaultMessage="on your iPhone"
                    />
                </p>
                <hr className={styles.phoneBar} />
                <p className={styles.textProxima}>
                    <FormattedMessage
                        id="checkout.CheckoutProcessingAdPage.cta"
                        defaultMessage="Download our mobile app, available on the app store."
                    />
                </p>
            </div>
        </div>
    );

    let bottomContent = null;

    if (shouldRenderAppAd) {
        bottomContent = renderAppAd;
    } else if (!isMultiSku) {
        bottomContent = renderDefault;
    }

    const topContent = (
        <div className={styles.processingContainer}>
            <LocalSpinner wrapperClass={styles.spinner} />
            <div className={styles.processingMessage}>
                <FormattedMessage
                    id="checkout.CheckoutProcessingPage.processingMessage"
                    defaultMessage="One moment, please do not refresh while we process your request."
                />
            </div>
        </div>
    );

    return (
        <div className={styles.wrapper}>
            {isMultiSku ? (
                <div className={styles.multiSkuTopSection}>{topContent}</div>
            ) : (
                <div className={styles.topSection}>{topContent}</div>
            )}
            <div className={styles.bottomSection}>{bottomContent}</div>
        </div>
    );
};

export default CheckoutProcessingPage;
